import {Fragment, useEffect, useReducer, useState} from "react";
import React from 'react';
import ScratchClubHeader from "../../components/headers/scratchClub/scratchClubHeader";
import RecordAndTrackHeader from "../../components/headers/recordAndTrack/recordAndTrackHeader";
import CourseTable from "../../components/tables/courseTable";
import CourseGrid from '../../components/graphs/ResultsGraph';
import TrainingChallenges from "../../components/trainingChallenges/trainingChallenges";
import fetchData from "./getScoreData";
import {recordScore} from "../../interfaces/recordScore";
import {axiosRequest} from "../../helpers/axios";
import {Lesson} from "../../interfaces/lesson";
import {useAccelerator} from "../../hooks/acceleratorDataProvider";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import dayjs, {Dayjs} from "dayjs";
import PageLoader from "../../components/loader/loader";
import {pageViewEvent} from "../../components/mixPanelEvents/mixPanelEvents";
import { useLocation } from "react-router-dom";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import StatsPanel from "../../components/cards/statsPanel/statsPanel";
import ScoreCardForm from "../../components/dialogs/scoreCardDialog/scoreCardForm";
import {
    UseiGolfData,
    UseiGolfCourseTeeDetails,
    UseiGolfGetStates
} from '../../hooks/useiGolfData';
import {axiosRequestData} from "../../helpers/axios";
import {scorecardAddEvent} from "../../components/mixPanelEvents/mixPanelEvents";
import { extractActivePropertyLabels } from '../../helpers/utils';
import {iGolfCourseTee} from "../../interfaces/iGolfCourseTee";
import {iGolfCourse} from "../../interfaces/iGolfCourse";

function reducer(records: recordScore[], action: { type: string, payload: any }) {
    switch (action.type) {
        case 'set' : {
            return action.payload
        }
        case 'add' : {
            if (action.payload.record_date === dayjs(new Date()).format("YYYY-MM-DD")) {
                return [action.payload, ...records]
            } else {
                let firstDate = records.find(el => new Date(el.record_date) <= new Date(action.payload.record_date));
                if (firstDate) {
                    let dateIndex = records.indexOf(firstDate);
                    records.splice(dateIndex, 0, action.payload);

                    return [...records];
                }
            }

            return [...records, action.payload]
        }
        case 'remove' : {
            return records.filter(el => el.record_id !== action.payload)
        }
        case 'edit' : {
            let modifiedRecords = records.map(el => el.record_id === action.payload.record_id ? action.payload : el);
            modifiedRecords.sort(function (a, b) {
                const previousDate = new Date(a.record_date);
                const nextDate = new Date(b.record_date);
                if (previousDate < nextDate)
                    return 1
                if (previousDate > nextDate)
                    return -1;

                return 0
            });

            return [...modifiedRecords]
        }
        default : {
            return records;
        }
    }
}

const RecordAndTrack = () => {
    const [records, dispatch] = useReducer(reducer, []);
    const [drillLessons, setDrillLessons] = useState<Lesson[]>([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const {loaded} = useAccelerator();
    const [filterDate, setFilterDate] = useState<[Date, Date]>([
        startOfMonth(addMonths(new Date(), -2)),
        endOfMonth(new Date())
    ]);
    const [submitScoreCardDialog, setSubmitScoreCardDialog] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const {setAverageScore} = useAccelerator();

    // ScoreCardForm states
    const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs(new Date()));
    const [score, setScore] = useState("");
    const [courseName, setCourseName] = useState("");
    const [fairway, setFairway] = useState("");
    const [greensInRegulation, setGreensInRegulation] = useState("");
    const [numberOfPutts, setNumberOfPutts] = useState("");
    const [upAndDowns, setUpAndDowns] = useState("");
    const [chippings, setChippings] = useState("");
    const [mental, setMental] = useState("");
    const [strokes, setStrokes] = useState("");
    const [holes, setHoles] = useState("");
    const [expanded, setExpanded] = useState(false);
    const [recordID, setRecordID] = useState(0);
    const [loading, setLoading] = useState(false);
    const [numHoles, setNumHoles] = useState<string>('18');
    const { courseList, setCourseList, searchiGolfCourses, setSelectedCourse, selectedCourse, loadingiGolf } = UseiGolfData();
    const { teeDetails, setTeeDetails, getTeeDetails, loadingTee, selectedTee, setSelectedTee, getTeeDetailsText, gender, setGender } = UseiGolfCourseTeeDetails();
    const { countryStateList, getStateListByCountries } = UseiGolfGetStates();
    const [iGolfTeeText, setiGolfTeeText] = useState<string>('');
    const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

    const location = useLocation();

    useEffect(function loadAllStates() {
        getStateListByCountries();
        const localGender = localStorage.getItem('iGolfGender');
        if (localGender) {
            setGender(localGender);
        }
    }, []);

    useEffect(function loadiGolfTeeDetails(){
        if (selectedCourse) {
            getTeeDetails(selectedCourse.id_course);
        }
    }, [selectedCourse]);

    useEffect(function saveLastGender() {
        localStorage.setItem("iGolfGender", gender);
    }, [gender]);

    const handleSubmitScoreCard = () => {
        if (isMobile) {
            setSubmitted(true);
        } else {
            setSubmitScoreCardDialog(true);
        }
    };

    const valuesPopulated = () => {
        return !!(score && courseName && selectedTee && dateValue?.isValid())
    }

    const handleTeeSelectChange = (event: any) => {
        const selectedValue = event.target.value;
        setiGolfTeeText(selectedValue);
    }

    const handleTeeChange = (tee: iGolfCourseTee) => {
        setSelectedTee(tee);
    };

    const setHolesByScore = (e: any) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            const scoreValue = parseInt(e.target.value);
            if (scoreValue < 60) {
                setNumHoles('front9');
            }
        }, 750);
    };

    const resetForm = () => {
        setScore("");
        setDateValue(dayjs(new Date()));
        setCourseName("");
        setUpAndDowns("");
        setChippings("");
        setNumberOfPutts("");
        setMental("");
        setStrokes("");
        setHoles("");
        setFairway("");
        setGreensInRegulation("");
        setSelectedCourse(null);
        setCourseList([]);
        setTeeDetails([]);
        setSelectedTee(null);
        setiGolfTeeText('');
        setNumHoles('18');
    }

    const submitScoreCard = () => {
        const requestData: recordScore = {
            score: Number(score),
            record_date: dateValue ? dateValue.format("YYYY-MM-DD").toString() : "",
            course: courseName,
            chipping_bunkers_1: upAndDowns,
            chipping_bunkers_2: chippings,
            putting: numberOfPutts,
            mental_game: mental,
            wedge_game_1: strokes,
            wedge_game_2: holes,
            driving: fairway,
            ball_striking: greensInRegulation,
            record_id: Number(recordID),
            tee: selectedTee,
            gender: gender,
            courseObj: selectedCourse,
            numHoles: numHoles
        }
        const statsIncluded = extractActivePropertyLabels(requestData);
        scorecardAddEvent(statsIncluded)

        setLoading(true);
        axiosRequestData("POST", "/wp-json/pg/v2/sc/score-tracker", requestData)
            .then(response => {
                if (response && typeof response.data.data.avg === 'number') {
                    setAverageScore(response.data.data.avg);
                }
                setSubmitted(false);
                resetForm();
                loadDataByDate();
            })
            .finally(() => setLoading(false))
    }
   
    const passedLesson = location.state

    useEffect(() => {
        loadDataByDate();
    }, [filterDate]);

    const loadDataByDate = () => {
        fetchData(dayjs(filterDate[0]).format("YYYY-MM-DD"), dayjs(filterDate[1]).format("YYYY-MM-DD"))
            .then((response) => {
                dispatch({type: 'set', payload: response.data.data});


                setDataLoaded(true);
            })
            .catch((error) => {
                console.log("Fetching the score data error:", error);
            });
    }

    useEffect(() => {
        axiosRequest("GET", "/wp-json/pg/v2/sc/drills")
            .then(response => {
                setDrillLessons(response.data.data.data)
            })
            .catch((error) => {
                console.log("Fetching drill data error:", error);
            });
    }, []);

    useEffect(() => {
        pageViewEvent('Record and Track')
    }, []);

    useEffect(function submitScoreCard() {
        if (!submitScoreCardDialog) return;
        setSubmitScoreCardDialog(false);

        loadDataByDate();
    }, [submitScoreCardDialog]);

    const checkScratchClubAccessFromScCatalog = () => {
        if (typeof window !== 'undefined' && window.pgUser?.products) {
          return window.pgUser.products.some((product: string) => product === '1' || product === 'Scratch Club'); // Scratch Club product ID
        }
        return false;
      };

    return (
        <Fragment>
            {isMobile && submitted ? (
                <Box sx={{ 
                    paddingLeft: { xs: '15px', md: '50px' }, 
                    paddingRight: { xs: '15px', md: '50px' },
                    paddingTop: '55px',
                    paddingBottom: '20px',
                    '@media (min-width: 450px) and (max-width: 900px)': {
                        paddingLeft: '30px',
                        paddingRight: '30px',
                    }
                }}>
                    <ScoreCardForm
                        dateValue={dateValue}
                        setDateValue={setDateValue}
                        score={score}
                        setScore={setScore}
                        courseName={courseName}
                        setCourseName={setCourseName}
                        fairway={fairway}
                        setFairway={setFairway}
                        greensInRegulation={greensInRegulation}
                        setGreensInRegulation={setGreensInRegulation}
                        numberOfPutts={numberOfPutts}
                        setNumberOfPutts={setNumberOfPutts}
                        upAndDowns={upAndDowns}
                        setUpAndDowns={setUpAndDowns}
                        chippings={chippings}
                        setChippings={setChippings}
                        mental={mental}
                        setMental={setMental}
                        strokes={strokes}
                        setStrokes={setStrokes}
                        holes={holes}
                        setHoles={setHoles}
                        expanded={expanded}
                        setExpanded={setExpanded}
                        numHoles={numHoles}
                        setNumHoles={setNumHoles}
                        iGolfTeeText={iGolfTeeText}
                        setiGolfTeeText={setiGolfTeeText}
                        selectedCourse={selectedCourse}
                        setSelectedCourse={setSelectedCourse}
                        selectedTee={selectedTee}
                        setSelectedTee={setSelectedTee}
                        gender={gender}
                        setGender={setGender}
                        courseList={courseList}
                        searchiGolfCourses={searchiGolfCourses}
                        countryStateList={countryStateList}
                        teeDetails={teeDetails}
                        loadingiGolf={loadingiGolf}
                        loadingTee={loadingTee}
                        getTeeDetailsText={getTeeDetailsText}
                        handleTeeSelectChange={handleTeeSelectChange}
                        handleTeeChange={handleTeeChange}
                        setHolesByScore={setHolesByScore}
                        valuesPopulated={valuesPopulated}
                        submitScoreCard={submitScoreCard}
                        loading={loading}
                        onCancel={() => setSubmitted(false)}
                        showDialog={true}
                        isInDialog={false}
                    />
                </Box>
            ) : (
                <>
                    <ScratchClubHeader/>

                    {(!loaded || !dataLoaded) && <PageLoader/>}

                    {loaded && dataLoaded &&
                        <Box sx={{ 
                            paddingLeft: { xs: '15px', md: '50px' }, 
                            paddingRight: { xs: '15px', md: '50px' } 
                        }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={12}>
                                    <RecordAndTrackHeader 
                                        recordDispatcher={dispatch} 
                                        date={filterDate}
                                        setDate={(value: [Date, Date]) => setFilterDate(value)}
                                        onSubmitClick={handleSubmitScoreCard}
                                    />
                                </Grid>
                                {/* <Grid item xs={12} md={4} id={"pg-stats-panel"} sx={{marginTop: {xs: "20px", md: "0"}}}>
                                    <Box sx={{ maxWidth: '460px', marginLeft: 'auto' }}>
                                        <StatsPanel setSubmitScoreCardDialog={isMobile ? handleSubmitScoreCard : setSubmitScoreCardDialog}/>
                                    </Box>
                                </Grid> */}
                            </Grid>
                            <Box
                                onClickCapture={(event) => {
                                    if (!checkScratchClubAccessFromScCatalog()) {
                                        event.stopPropagation();
                                        event.preventDefault();
                                        window.location.href = '/scratch-club-benefits/';
                                        return false; 
                                    }
                                    return true;
                                }}
                            >
                                <Box id={"pg-course-table"}>
                                    <Typography className="d-md-block d-none" sx={{
                                        fontWeight: 500,
                                        fontSize: "22px",
                                        margin: "24px 0",
                                        fontFamily: "'Repro', sans-serif",
                                        textTransform: "uppercase"
                                    }}>Training Challenges</Typography>
                                    <CourseTable records={records} recordDispatcher={dispatch} date={filterDate} setDate={(value: [Date, Date]) => setFilterDate(value)}/>
                                    <CourseGrid records={records} />
                                </Box>
                                <TrainingChallenges drillLessons={drillLessons} passedLesson={passedLesson}/>
                            </Box>
                        </Box>
                    }
                </>
            )}
        </Fragment>
    );
};


export default RecordAndTrack;